import { useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router";

import { Box } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { scrollWithOffset } from "@/components/pages_logged_in/program/components/component-utils";
import { ProgramDashboard } from "@/components/pages_logged_in/program/components/ProgramDashboard";
import { ProgramDetailsHeader } from "@/components/pages_logged_in/program/components/ProgramDetailsHeader";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { Container } from "@/components/pieces/bootstrap";
import { HEADER_HEIGHT } from "@/components/pieces/menu/MenuHeader";
import { getRefreshToken } from "@/components/session/JwtTokenManager";
import { SIDE_NAV_WIDTH } from "@/css/constants";
import { useProgramComponentsQuery, useProgramQuery } from "@/graphql";

function AuthProgramDashboard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [errorMessage, setErrorMessage] = useState<string>();
  const hashData = window.location.hash.slice(1);
  const hasAttemptedRedirect = useRef(false);
  const { currentUser } = useAuth();

  const { data, loading, refetch } = useProgramQuery({
    variables: {
      programId: id || "",
    },
    onError: (error) => {
      setErrorMessage(error?.message || "Cannot get program data");
    },
    skip: !id,
  });

  const {
    data: componentsData,
    loading: loadingComponent,
    refetch: refetchProgramComponents,
  } = useProgramComponentsQuery({
    fetchPolicy: "network-only",
    variables: {
      programId: id || "",
      userId: currentUser.id,
    },
    onError: (error) => {
      setErrorMessage(error?.message || "Cannot get program Component");
    },
  });

  useEffect(() => {
    if (hashData) {
      // setTimeout to wait render components
      const timeout = setTimeout(() => {
        scrollWithOffset({
          componentTag: hashData,
          offset: -(HEADER_HEIGHT + 30),
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [hashData]);

  // Handle user activation redirect with protection against infinite loops
  useEffect(() => {
    if (!currentUser.name && !hasAttemptedRedirect.current) {
      const token = getRefreshToken();
      hasAttemptedRedirect.current = true;
      navigate(`/activate-participant/${id}/${token}?next=/programs/${id}`);
    }
  }, [currentUser, id, navigate]);

  if ((loading && !data) || (loadingComponent && !componentsData)) {
    return <ProgramDetailsLoader includeSidebar={true} />;
  }

  if (!data?.program || !componentsData || errorMessage) {
    return <PermissionErrorPage />;
  }

  return (
    <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
      <Nav />
      <ProgramDetailsHeader program={data?.program} refetchProgram={refetch} />
      <Container mb={2}>
        <ProgramDashboard
          program={data.program}
          componentsData={componentsData}
          refetchProgram={refetch}
          refetchProgramComponents={refetchProgramComponents}
        />
      </Container>
    </Box>
  );
}

export function ProgramDashboardPage() {
  return (
    <Authenticate maxWidth="100%" layout="full">
      <AuthProgramDashboard />
    </Authenticate>
  );
}
